import {Trans} from '@lingui/macro';
import * as t from 'io-ts';
import {sortBy} from 'lodash';
import React, {ReactNode} from 'react';

import {
  collectEnumValues,
  makeEnumRuntimeType,
} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {RuleBaseModelStrict} from '../../../models/api/ruleModel';
import {RuleActionTypesEnum} from '../ruleActionModel';

export enum RuleBundleCategoriesEnum {
  ASSIGN = 'assign',
  ARCHIVE = 'archive',
  COMMENT = 'comment',
  MOVE = 'move',
  NOTIFY = 'notify',
  REPLY = 'reply',
  TAG = 'tag',
  SLA = 'sla',
}

export const sortedRuleBundleCategories = sortBy(
  collectEnumValues(RuleBundleCategoriesEnum).map((v) => ({key: v})),
  (v) => v.key,
);

export const RULE_TYPES_LABELS: Record<RuleBundleCategoriesEnum, ReactNode> = {
  [RuleBundleCategoriesEnum.ASSIGN]: <Trans id="rule-types-assign-label">Assign</Trans>,
  [RuleBundleCategoriesEnum.ARCHIVE]: <Trans id="rule-types-archive-label">Archive</Trans>,
  [RuleBundleCategoriesEnum.COMMENT]: <Trans id="rule-types-comment-label">Comment</Trans>,
  [RuleBundleCategoriesEnum.MOVE]: <Trans id="rule-types-move-label">Move</Trans>,
  [RuleBundleCategoriesEnum.NOTIFY]: <Trans id="rule-types-notify-label">Notify</Trans>,
  [RuleBundleCategoriesEnum.REPLY]: <Trans id="rule-types-reply-label">Reply</Trans>,
  [RuleBundleCategoriesEnum.TAG]: <Trans id="rule-types-tag-label">Tag</Trans>,
  [RuleBundleCategoriesEnum.SLA]: <Trans id="rule-types-sla-label">SLA</Trans>,
};

export function getRuleType(rule: RuleBaseModelStrict): RuleBundleCategoriesEnum | null {
  const actionTypes = [...new Set(rule.actions.map((a) => a.type))];

  if (
    actionTypes.every(
      (action) =>
        action === RuleActionTypesEnum.NOTIFY_TEAMMATES ||
        action === RuleActionTypesEnum.NOTIFY_ASSIGNEE ||
        action === RuleActionTypesEnum.NOTIFY_ACCOUNT_FIELD,
    )
  ) {
    return RuleBundleCategoriesEnum.NOTIFY;
  }

  if (actionTypes.every((action) => action === RuleActionTypesEnum.MOVE)) {
    return RuleBundleCategoriesEnum.MOVE;
  }

  if (
    actionTypes.every(
      (action) =>
        action === RuleActionTypesEnum.ASSIGN ||
        action === RuleActionTypesEnum.RANDOM_ASSIGN ||
        action === RuleActionTypesEnum.ASSIGN_CONTACT_FIELD ||
        action === RuleActionTypesEnum.ASSIGN_ACCOUNT_FIELD,
    )
  ) {
    return RuleBundleCategoriesEnum.ASSIGN;
  }

  if (actionTypes.every((action) => action === RuleActionTypesEnum.ADD_COMMENT)) {
    return RuleBundleCategoriesEnum.COMMENT;
  }

  if (actionTypes.every((action) => action === RuleActionTypesEnum.ARCHIVE)) {
    return RuleBundleCategoriesEnum.ARCHIVE;
  }

  if (actionTypes.every((action) => action === RuleActionTypesEnum.REPLY)) {
    return RuleBundleCategoriesEnum.REPLY;
  }

  if (
    actionTypes.every((action) => action === RuleActionTypesEnum.TAG || action === RuleActionTypesEnum.UNTAG)
  ) {
    return RuleBundleCategoriesEnum.TAG;
  }

  return null;
}

export const RRuleBundleCategoriesEnum = makeEnumRuntimeType(RuleBundleCategoriesEnum);

export const RSettingsRuleListFiltersState = t.readonly(
  t.partial({
    query: t.string,
    categories: t.readonlyArray(RRuleBundleCategoriesEnum),
    // The value for onlySmart is either true or undefined. We never (currently) want to show
    // only non-smart rules.
    onlySmart: t.boolean,
    inbox: t.Integer,
  }),
);

export interface SettingsRuleListFiltersState extends t.TypeOf<typeof RSettingsRuleListFiltersState> {}
